const PRINT_PICKER_ELM_NAME = 'pick-your-print';
const PRINT_PICKER_SLIDER_ELM_NAME = `${PRINT_PICKER_ELM_NAME}-slider`;
const PRINT_PICKER_SLIDER_ELM_SELECTOR = `[data-${PRINT_PICKER_SLIDER_ELM_NAME}]`;
const PRODUCT_SLIDER_ELM_NAME = 'product-slider';
const PRODUCT_SLIDER_ELM_SELECTOR = `[data-${PRODUCT_SLIDER_ELM_NAME}]`;
const PRINT_PICKER_BUTTON_SELECTOR = '[data-pick-your-print-button]';
const GROUP_PRINT_PICKER = 'group\\/pick-your-print';
const GROUP_PRINT_PICKER_CLASS = `.${GROUP_PRINT_PICKER}`;
const ACTIVE_GROUP_PRINT_PICKER = 'active-group-print-picker';

// Default to improve user experience
const swiperDefaults = {
  threshold: 10,
};

(async () => {
  if (!customElements.get(PRINT_PICKER_ELM_NAME)) {
    const { default: Swiper, A11y, Navigation, Scrollbar, Autoplay } = await import('swiper');

    Swiper.use([A11y, Navigation, Scrollbar, Autoplay]);

    class PrintPicker extends HTMLElement {
      printPickerSlider = null;
      productSlider = null;
      printPickerButtons = null;
      primaryCTA = null;
      selectedButtonGroup = null;
      printPickerSwiperOptions = {
        ...swiperDefaults,
      };
      productSliderSwiperOptions = {
        ...swiperDefaults,
      };
      /** @type {import('swiper').Swiper} */
      printPickerSwiperInstance = null;

      /** @type {import('swiper').Swiper} */
      productSliderSwiperInstance = null;

      constructor() {
        super();

        this.printPickerSlider = this.querySelector(PRINT_PICKER_SLIDER_ELM_SELECTOR);
        this.productSlider = this.querySelector(PRODUCT_SLIDER_ELM_SELECTOR);
        this.printPickerButtons = this.querySelectorAll(PRINT_PICKER_BUTTON_SELECTOR);

        const primaryCTA = this.querySelector('[data-primary-cta]');

        if (primaryCTA) {
          this.primaryCTA = primaryCTA;
        }

        // Stop when either slider cannot be found
        if (!this.printPickerSlider || !this.productSlider || !this.printPickerButtons.length) {
          return;
        }

        // Check if we have extra options on the HTML
        if (this.printPickerSlider.dataset.options) {
          const options = JSON.parse(this.printPickerSlider.dataset.options);
          if (options) {
            this.printPickerSwiperOptions = {
              ...this.printPickerSwiperOptions,
              ...options,
            };
          }
        }

        if (this.productSlider.dataset.options) {
          const options = JSON.parse(this.productSlider.dataset.options);
          if (options) {
            this.productSliderSwiperOptions = {
              ...this.productSliderSwiperOptions,
              ...options,
            };
          }
        }

        // Call swiper with selected swiper element and options
        this.printPickerSwiperInstance = new Swiper(
          this.printPickerSlider,
          this.printPickerSwiperOptions
        );
        this.productSliderSwiperInstance = new Swiper(
          this.productSlider,
          this.productSliderSwiperOptions
        );

        this.attachButtonHandlers();

        /**
         * Listen to extra events when in Shopify editor
         */
        if (Shopify.designMode) {
          // Update the swiper when the section event is triggerd
          window.addEventListener('shopify:section:load', (event) => {
            this.handleDesignMode(false, event);
          });

          // When on block select go to the slide in front-end
          window.addEventListener('shopify:block:select', (event) => {
            this.handleDesignMode(true, event);
          });

          // When on block deselect go to the slide in front-end
          window.addEventListener('shopify:block:deselect', (event) => {
            this.handleDesignMode(true, event);
          });
        }
      }

      handleDesignMode = (shouldHandleBlockSelect, event) => {
        this.printPickerSwiperInstance.update();
        this.productSliderSwiperInstance.update();

        if (this.printPickerSwiperOptions.navigation) {
          this.printPickerSwiperInstance.navigation.init();
          this.printPickerSwiperInstance.navigation.update();
        }

        if (this.productSliderSwiperOptions.navigation) {
          this.productSliderSwiperInstance.navigation.init();
          this.productSliderSwiperInstance.navigation.update();
        }

        if (shouldHandleBlockSelect) {
          this.handleBlockSelect(event);
        }
      };

      attachButtonHandlers() {
        this.printPickerButtons.forEach((button) => {
          button.addEventListener('click', this.handlePrintPickerClick);
        });

        if (!this.selectedButtonGroup && this.printPickerButtons.length) {
          this.addPrintPickerActiveClasses(this.printPickerButtons[0]);
        }
      }

      addPrintPickerActiveClasses(button) {
        const group = button.closest(GROUP_PRINT_PICKER_CLASS);

        if (this.selectedButtonGroup) {
          this.selectedButtonGroup.classList.remove(ACTIVE_GROUP_PRINT_PICKER);
        }

        if (group) {
          group.classList.add(ACTIVE_GROUP_PRINT_PICKER);

          this.selectedButtonGroup = group;
        }
      }

      handlePrintPickerClick = (event) => {
        const { currentTarget } = event;
        const handle = currentTarget.getAttribute('data-print-collection-handle');

        if (handle) {
          this.printPickerButtons.forEach((button) => {
            button.setAttribute('disabled', 'disabled');
          });

          fetch(`/collections/${handle}?section_id=pick-your-print-dynamic`, {
            cache: 'force-cache',
          }).then((response) => {
            response
              .text()
              .then((text) => {
                const parser = new DOMParser();
                const htmlDocument = parser.parseFromString(text, 'text/html');

                const productSliderClone = this.productSlider.cloneNode(true);
                const slidesWrapper = productSliderClone.querySelector('[data-slides-wrapper]');
                const content = htmlDocument.body.children[0]?.children;
                const productSliderParent = this.productSlider?.parentElement;

                if (content?.length && productSliderParent) {
                  this.productSliderSwiperInstance.destroy();

                  slidesWrapper.replaceChildren(...content);
                  productSliderParent.replaceChild(productSliderClone, this.productSlider);

                  this.productSliderSwiperInstance = new Swiper(
                    productSliderClone,
                    this.productSliderSwiperOptions
                  );

                  this.productSlider = productSliderClone;

                  if (this.primaryCTA) {
                    this.primaryCTA.setAttribute('href', `/collections/${handle}`);
                  }
                }
              })
              .finally(() => {
                this.printPickerButtons.forEach((button) => {
                  button.removeAttribute('disabled');
                });
              });
          });

          this.addPrintPickerActiveClasses(currentTarget);
        }
      };

      /**
       * Handles the theme editor block change/edit event
       * @param {Object} event
       */
      handleBlockSelect(event) {
        // Check if the slide index is set
        if (!('swiperSlideIndex' in event.target.dataset)) {
          return;
        }

        // Set the slide index based on loop settings or not
        let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
        if (this.printPickerSwiperOptions.loop) {
          swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
        }

        // Slide to slide based on the data attribute from the target
        const sliderToUpdate = event.target.closest(PRINT_PICKER_SLIDER_ELM_SELECTOR);
        sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
      }
    }

    window.PrintPicker = PrintPicker;

    customElements.define(PRINT_PICKER_ELM_NAME, PrintPicker);
  }
})();
